/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {ColDef, ValueGetterParams} from 'ag-grid-community';
import {
  loadDeviceDetail,
  viewAdminDevice,
  viewDevice
} from '@ion/applications/src/pages/devices/state';
import {translate} from '@ion/core/src/locale/locale';
import {getCodeMappings, getErrorCode, hasDiagCode} from '@ion/core/src/services/JobMetaDefinitions';
import SmartSearchPaging from '@ion/core/src/services/SmartSearchPaging';
import {
  ApplicationFeatures,
  CRUDPermissions,
  Device,
  DownloadJob,
  GenericObject
} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import LinkCell from '@ion/dls/src/components/grid/LinkCell';
import IonGrid, {AgGridType, IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import {SortModelType} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import useColumnChooser from '@ion/dls/src/grid/useColumnChooser';
import useColumnState from '@ion/dls/src/grid/useColumnState';
import {DLSTheme} from '@ion/dls/src/types/types';
import {UsePaginationResult} from '@ion/query/src/hooks/usePagination';
import JobMeta from '../jobs/JobMeta';
import {JobStatusCell} from './cells/JobStatusCell';
import {DEFAULT_PAGINATION} from './signals/paginationState';
import {JobSearchEngineRowEditor} from './JobSearchEngineRowEditor';
import {sortModelConverter} from './JobSearchEngineUtils';
import {JobStatus} from './jobStatusBadge';
import {useJobGridPagination} from './useJobGridPagination';
import {useJobPagination} from './useJobPagination';

const DISTANCE_FROM_TOP = 474;
const CUSTOM_HEIGHT_DEFAULT = 94;

const useStyles = makeStyles((theme: DLSTheme) => ({
  devicesGrid : {
    '& .ag-center-cols-viewport' : {
      overflowX : 'hidden !important'
    },
    '& .ag-popup' : {
      height : 'auto'
    },
    '&&' : {
      height : ({customHeight = CUSTOM_HEIGHT_DEFAULT}: {customHeight: number;}) =>
        `calc(100vh - ${customHeight + DISTANCE_FROM_TOP}px)`,
      minHeight                              : '156px !important',
      '& .ag-body-viewport.ag-layout-normal' : {
        overflowY : 'auto'
      }
    }
  }
}));

type TaskDevicesGridProps = {
  jobs?: DownloadJob,
  jobId: number,
  activeTab: string,
  customHeight: number,
  taskIndex: number,
  onRefresh: Function,
  refresh: boolean,
  containerHeight?: boolean,
  onSettled?: () => void,
  getDevices: (arg: {
    page?: number; pageSize?: number; sort?: string[];
    filter?: GenericObject; callback: Function;
  }) => void;
  jobsPermissions?: CRUDPermissions;
  Features?: ApplicationFeatures;
  setIsTaskDeviceClicked?: Function;
  setEnableApi?: Function;
  tabPaging: UsePaginationResult;
};

export type TaskDevicesGridAPI = {
  onRefresh: Function,
  refreshInfiniteCache: () => void
};

const defaultSort: SortModelType = {colId: 'deviceIdentifier', sort: 'desc'};

const attributeMappings = {
  status      : 'deviceTaskStatus',
  reason      : 'reason',
  identifier  : 'deviceIdentifier',
  lastUpdated : 'updatedOn'
};

/**
* The component that displays the devices details of selected task row from tasks Table.
*/
export const TaskDevicesGrid = ({
  jobs,
  jobId,
  activeTab,
  tabPaging,
  taskIndex,
  onRefresh,
  refresh,
  onSettled,
  Features,
  getDevices,
  jobsPermissions,
  setIsTaskDeviceClicked,
  customHeight = 0,
  containerHeight = false
}: TaskDevicesGridProps, ref) => {
  const classes    = useStyles({customHeight});
  const dispatch   = useDispatch();
  const ionGridRef = useRef<IonGridAPI>();
  const gridApiRef = useRef<AgGridType>();
  const maxLimit   = SmartSearchPaging.MAX_LIMIT;
  const {getPaginationForTab, clearPaginationForTab} = useJobPagination();

  const paginationForJobTab = getPaginationForTab(activeTab) ?? DEFAULT_PAGINATION;

  const {
    definitions, defaultDefinitions
  } = useColumnState({columnDefinitions: getColumnDefinitions(), columnState: null});

  const {columnDefinitions} = useColumnChooser(definitions);

  const refreshInfiniteCache = useCallback(() => {
    ionGridRef.current?.refreshInfiniteCache?.();
  }, []);

  useImperativeHandle(ref, () => ({
    onRefresh,
    refreshInfiniteCache
  }));

  useEffect(() => {
    onRefresh();
  }, [jobId, taskIndex]);

  useEffect(() => {
    if (activeTab && !getPaginationForTab(activeTab)) {
      clearPaginationForTab(activeTab);
    }
  }, [activeTab]);

  const columnComponents = useMemo(() => ({
    deviceCell : (item: GridResult) => (
      <LinkCell data={item} textTransform={'none'} onClick={onDeviceClick(item?.data)} />
    ),
    deviceStatusCell : ({data}) => {
      return (data?.status
        ? <JobStatusCell status={JobStatus[data?.status === 'SUCCESS' ? 'COMPLETED' : data?.status]} />
        : '');
    },
    reasonCell : (item: GridResult) => {
      return (
        <JobMeta data={item} />
      );
    },
    rowMenu : item => (
      <JobSearchEngineRowEditor device={item?.data}
        job={jobs}
        Features={Features}
        jobsPermissions={jobsPermissions}
        hasDevice />
    )
  }), []);

  const getGridHeight = useCallback(function() {
    return classes.devicesGrid;
  }, []);

  function constructTaskDevicesGridRows(devices) {
    return devices?.map((device) => {
      return {
        ...device,
        device      : device?.device,
        identifier  : device?.device?.esn || device?.device?.serialNumber || device?.identifier,
        status      : device?.taskStatus?.status,
        reason      : device?.taskStatus?.failureReason || device?.taskStatus?.reason || '',
        lastUpdated : formatDateTime(device?.taskStatus?.updateTime) || translate('common:notAvailable')
      };
    });
  };

  const onDeviceClick = useCallback((data: Device) => {
    return function() {
      onRowDoubleClicked({data});
    };
  }, []);

  // invokes to load device view dialog on row double click or any provided grid link
  const onRowDoubleClicked = useCallback(({data: {device}} = {}) => {
    if (device) {
      dispatch(viewDevice(device) as any);
      dispatch(loadDeviceDetail(device));
      dispatch(viewAdminDevice(device));
      setIsTaskDeviceClicked?.(true);
    }
  }, []);

  const gotoPage = useCallback((page: number) => {
    gridApiRef.current?.paginationGoToPage(page);
  }, []);

  const purgeCache = useCallback(() => {
    gridApiRef.current?.purgeInfiniteCache?.();
  }, []);

  const onGridReady = useCallback((api: AgGridType) => {
    gridApiRef.current = api;
  }, []);

  const {wrapLoadItems} = useJobGridPagination({activeTab, tabPaging, gotoPage, purgeCache});

  const wrappedGetDevices = useMemo(
    () => wrapLoadItems(getDevices, {onSettled}),
    [wrapLoadItems, getDevices, onSettled]
  );

  return (
    <IonGrid ref={ionGridRef}
      paging={tabPaging}
      pageSize={paginationForJobTab.pageSize}
      onGridReady={onGridReady}
      paginationEnabled
      pageConstraintProps={{
        constraint : maxLimit,
        entity     : translate('common:entityLabels.device')
      }}
      checkboxesEnabled={false}
      refreshRequired={refresh}
      showCustomPaginationBar
      loadItems={wrappedGetDevices}
      maxBlocksInCache={1}
      defaultGridMinHeight={100}
      attributeMappings={attributeMappings}
      sortModelConverter={sortModelConverter}
      defaultSort={defaultSort}
      getGridHeight={getGridHeight}
      augmentItems={constructTaskDevicesGridRows}
      columnComponents={columnComponents}
      columnDefinitions={columnDefinitions}
      defaultDefinitions={defaultDefinitions}
      pageSizes={PAGE_SIZES}
      onRowDoubleClicked={onRowDoubleClicked}
      enableCellTextSelection />
  );
};

const reasonValue = (params: ValueGetterParams): string => {
  const rowData        = params.data ?? {};
  const firstErrorCode = rowData?.taskStatus?.errorCodes?.[0];
  const applicationId  = rowData?.device?.extensions?.appId;
  if (firstErrorCode) {
    const {statCode, diagCode, diagCode32Bit} = getCodeMappings() || {};
    if (hasDiagCode(firstErrorCode)) {
      const mappedError = getErrorCode({
        error : firstErrorCode,
        appId : applicationId,
        diagCode32Bit,
        statCode,
        diagCode
      });
      if (mappedError) {
        return mappedError;
      } else {
        return '';
      }
    }
    return firstErrorCode;
  }
  return '';
};

const getColumnDefinitionDesktop = (): ColDef[] => {
  return [{
    headerName         : translate('jobsearchengine:deviceGrid.columns.identifier'),
    field              : 'identifier',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  }, {
    headerName         : translate('jobsearchengine:deviceGrid.columns.status'),
    field              : 'status',
    cellRenderer       : 'deviceStatusCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  }, {
    headerName         : translate('jobsearchengine:deviceGrid.columns.reason'),
    colId              : 'reason',
    valueGetter        : reasonValue,
    cellRenderer       : 'reasonCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  }, {
    headerName : translate('jobsearchengine:deviceGrid.columns.lastUpdated'),
    field      : 'lastUpdated',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }];
};

/**
 *
 * Returns column definitions for mobile grid implementation ...
 */
const getColumnDefinitionMobile = (): ColDef[] => {
  return [{
    headerName         : translate('jobsearchengine:deviceGrid.columns.identifier'),
    field              : 'identifier',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  }, {
    headerName         : translate('jobsearchengine:deviceGrid.columns.status'),
    field              : 'status',
    cellRenderer       : 'deviceStatusCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  }, {
    headerName         : translate('jobsearchengine:deviceGrid.columns.reason'),
    colId              : 'reason',
    valueGetter        : reasonValue,
    cellRenderer       : 'reasonCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  }, {
    headerName : translate('jobsearchengine:deviceGrid.columns.lastUpdated'),
    field      : 'lastUpdated',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }];
};

const getColumnDefinitions = () => ({
  desktop : getColumnDefinitionDesktop(),
  mobile  : getColumnDefinitionMobile()
});

export default forwardRef(TaskDevicesGrid);
